import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { styled } from 'styled-components';
import { Root as ProgressRoot, Indicator as ProgressIndicator } from '@radix-ui/react-progress';
import { isNotNil } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { getColorScheme } from '../../private/helpers/getColorScheme';

const ProgressBarWrapper = styled.div`
  --progress-bar-height: 8px;

  display: flex;
  align-items: center;
  gap: var(--wui-space-02);
`;

// this function is necessary because there's a subpixel amount of indicator when progress is 0
const getTranslateValue = (progress: number, max: number): string => {
  if (progress === 0) {
    return 'translateX(-101%)'; // needs to be -101% to hide the subpixel amount
  }

  // convert progress to percentage relative to max
  const progressPercentage = (progress / max) * 100;
  return `translateX(-${100 - progressPercentage}%)`;
};

const ProgressBarLabel = styled.div`
  display: flex;
  line-height: var(--wui-typography-label-3-line-height);
  font-size: var(--wui-typography-label-3-size);
  font-weight: var(--wui-typography-weight-label);
  color: var(--wui-color-text-secondary);
  flex-shrink: 0;
`;

const StyledProgressIndicator = styled(ProgressIndicator)<{
  $max: number;
  $progress: number;
  $colorScheme: ColorSchemeTypes;
}>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  background-color: var(--wui-color-bg-fill);
  width: 100%;
  height: 100%;
  border-top-right-radius: var(--wui-border-radius-rounded);
  border-bottom-right-radius: var(--wui-border-radius-rounded);
  transition: transform 660ms cubic-bezier(0.65, 0, 0.35, 1);
  transform: ${({ $progress, $max }) => getTranslateValue($progress, $max)};
`;

type StyledProgressBarProps = {
  children: ReactNode;
  $colorScheme: ColorSchemeTypes;
};

const StyledProgressBar = styled(ProgressRoot)<StyledProgressBarProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)}
  position: relative;
  overflow: hidden;
  background-color: var(--wui-color-bg-surface-secondary);
  border-radius: var(--wui-border-radius-rounded);
  width: 100%;
  height: var(--progress-bar-height);

  /* Fix overflow clipping in Safari: https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0 */
  transform: translateZ(0);
`;

export type ProgressBarProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Current numeric level of the progress (0 -> max)
   */
  progress: number;
  /**
   * The maximum progress value
   */
  max?: number;
  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Slot for additional content to be rendered to the left of the progress bar
   */
  labelLeft?: ReactNode;
  /**
   * Slot for additional content to be rendered to the right of the progress bar
   */
  labelRight?: ReactNode;
};

/**
 * A progress component that visually represents the completion status of a task
 */
export const ProgressBar = ({
  progress,
  max = 100,
  children,
  labelLeft,
  labelRight,
  colorScheme = 'inherit',
  ...props
}: ProgressBarProps): JSX.Element => {
  return (
    <ProgressBarWrapper>
      {isNotNil(labelLeft) ? <ProgressBarLabel>{labelLeft}</ProgressBarLabel> : null}
      <StyledProgressBar
        $colorScheme={colorScheme}
        max={max}
        value={progress}
        {...props}
      >
        <StyledProgressIndicator
          $colorScheme={colorScheme}
          $max={max}
          $progress={progress}
        />
      </StyledProgressBar>
      {isNotNil(labelRight) ? <ProgressBarLabel>{labelRight}</ProgressBarLabel> : null}
    </ProgressBarWrapper>
  );
};

ProgressBar.displayName = 'ProgressBar';
